import { Injectable } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ApiService } from './api.service';
import { ROLE } from '../models/auth';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';
import {
  FileTemplateConfig,
  GetEntitlementsQuery,
  GetEntitlementsQueryVariables,
  GetOrganizationListQuery,
  GetOrganizationListQueryVariables,
  Organization,
  TargetColumnsConfig,
} from '../../../API';
import { GetEntitlements, GetOrganizationList } from '../../../graphql/queries';
import config from '../../app-config';
import { parseTargetColumnMapping } from '../utils/target-column-mapping.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public loginState = false;
  public errorState: { message: string } | null = null;
  private userInfo?: any;

  public entitlements: any[] = [];
  private userRole: string = '';

  orgList: Organization[] = [];
  private targetColumns: TargetColumnsConfig[] = [];
  private fileTemplateConfig: FileTemplateConfig[] = [];

  public readonly groupRoleMap: Record<string, string> = {
    'lsb-nfl-admin': ROLE.NFL_ADMIN,
    'lsb-club-admin': ROLE.CLUB_ADMIN,
  };

  constructor(
    private nzMessageService: NzMessageService,
    private apiService: ApiService,
  ) {}

  async logout() {
    await signOut();
    this.loginState = false;
  }

  /* @typescript-eslint/no-explicit-any */
  async fetchUserDetails(): Promise<any> {
    try {
      const user = await getCurrentUser();

      // Note that session will no longer contain refreshToken and clockDrift
      const { tokens: session, identityId } = await fetchAuthSession();

      const userGroups = (session?.accessToken?.payload['cognito:groups'] as Array<string>).filter(
        (group: string) => !group.endsWith('Okta'),
      );

      let attributes = await fetchUserAttributes();

      attributes = { orgId: attributes['custom:orgId'], ...attributes };
      delete attributes['custom:orgId'];

      this.userInfo = { ...user, ...attributes, identityId: identityId };

      await this.getEntitlements(userGroups[0]);
      this.setUserRole(userGroups);

      return true;
    } catch (err) {
      this.nzMessageService.error(`${err} while getting current user info`);
      return false;
    }
  }

  setUserRole(userGroups: string[] | null | undefined): void {
    if (!userGroups || userGroups.length === 0) {
      return;
    }

    const primaryGroup = userGroups[0];
    this.userRole = this.groupRoleMap[primaryGroup];
  }

  getUserInfo(): any {
    return this.userInfo;
  }

  getUserRole(): string {
    return this.userRole;
  }

  async validateLogin(): Promise<boolean> {
    try {
      await this.getOrgList();
      const authSession = await fetchAuthSession({ forceRefresh: true });
      if (authSession.credentials) {
        this.loginState = true;
      }
      return true;
    } catch (_err) {
      console.error('_err', _err);
      return false;
    }
  }

  public federatedOktaSignIn(): void {
    signInWithRedirect({
      provider: { custom: 'Okta' },
      customState: '/',
    }).catch((err) => {
      console.error('err', err);
    });
  }

  public getEntitlements(roleName: string): Promise<boolean> {
    return this.apiService
      .query<GetEntitlementsQuery, GetEntitlementsQueryVariables>(GetEntitlements, {
        role: roleName,
      })
      .then((result) => {
        if (result.data?.GetEntitlements?.entitlements) {
          this.entitlements.push(...result.data.GetEntitlements.entitlements);
        }
        return true;
      })
      .catch((err) => {
        console.error('Entitlement fetch failed', err);
        return false;
      });
  }

  public hasEntitlements(entitlements: string[]): boolean {
    return entitlements.some((entitlement) => this.entitlements.includes(entitlement));
  }

  public getStageBucket() {
    return config.stageBucket;
  }

  async getOrgList(): Promise<void> {
    return this.apiService
      .query<GetOrganizationListQuery, GetOrganizationListQueryVariables>(GetOrganizationList)
      .then((response) => {
        this.orgList = response.data?.GetOrganizationList || [];
      })
      .catch((err) => {
        console.warn('Error fetching organization: ', err);
      });
  }

  hasTargetColumnMappingEmpty(orgId: string): boolean {
    const targetColumnMapping = this.orgList?.find((org) => org.orgId === orgId)?.targetColumnMapping;
    return targetColumnMapping == '' || targetColumnMapping == null;
  }

  getTargetColumnMapping(): Record<string, string> {
    const targetColumnMapping = this.orgList?.find((org) => org.orgId === this.userInfo.orgId)?.targetColumnMapping;
    return parseTargetColumnMapping(targetColumnMapping);
  }

  getTargetColumns(): TargetColumnsConfig[] {
    this.orgList.forEach((org) => {
      if (org.orgId === this.userInfo.orgId) {
        this.targetColumns = org.targetColumns as TargetColumnsConfig[];
      }
    });

    return this.targetColumns;
  }

  getFileTemplateConfig(): FileTemplateConfig[] {
    this.orgList.forEach((org) => {
      if (org.orgId === this.userInfo.orgId) {
        this.fileTemplateConfig = org.fileTemplateConfig as FileTemplateConfig[];
      }
    });

    return this.fileTemplateConfig;
  }
}
